import {
    Alert,
    DataTable,
    Icon,
    IconButton,
    Loading,
    SearchInput,
    StyleFunction,
    Surface,
    Text,
    useAuth,
    useMerchantConfig,
    usePagination,
    useThemedStyle,
} from "@venuepos/react-common";
import { useShiftsQuery } from "graphql-sdk";
import { formatDateTime } from "lib";
import { formatAmount } from "lib/src/currencies/currencies";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";

import type { AvailableLocale } from "locales";
type ScreenProps = RootStackScreenProps<"SHIFTS">;

export function ShiftListScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.shift");

    const { currency } = useMerchantConfig();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "opened_at",
        initialSortDirection: "DESC",
    });
    const [search, setSearch] = useState<string>("");

    const { data, loading, error } = useShiftsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
        fetchPolicy: "no-cache",
    });

    const [{ locale }] = useAdminSession(["locale"]);

    function handleShowShift(id: string) {
        navigate("SHIFT", {
            id,
        });
    }

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list (in case the user changed to a different page)
            onPageChange(0);
        },
        [onPageChange]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <SearchInput onChange={handleSearchTextChange} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "department_name" && sortDirection
                            }
                            onPress={() => onSortChange("department_name")}
                        >
                            {t(
                                "backoffice.shifts.department_name",
                                "Department"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "cash_register_name" && sortDirection
                            }
                            onPress={() => onSortChange("cash_register_name")}
                        >
                            {t(
                                "backoffice.shifts.cash_register_name",
                                "Cash register"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "opened_at" && sortDirection
                            }
                            onPress={() => onSortChange("opened_at")}
                        >
                            {t("backoffice.shifts.opened_at", "Opened")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "closed_at" && sortDirection
                            }
                            onPress={() => onSortChange("closed_at")}
                        >
                            {t("backoffice.shifts.closed_at", "Closed")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "opened_by_username" && sortDirection
                            }
                            onPress={() => onSortChange("opened_by_username")}
                        >
                            {t(
                                "backoffice.shifts.opened_by_username",
                                "Opened by"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "closed_by_username" && sortDirection
                            }
                            onPress={() => onSortChange("closed_by_username")}
                        >
                            {t(
                                "backoffice.shifts.closed_by_username",
                                "Closed by"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            sortDirection={sortBy === "amount" && sortDirection}
                            onPress={() => onSortChange("amount")}
                        >
                            {t("shift.amount", "Amount")}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.sortIcon}>
                            <Icon name="sort" color={styles.sortIcon.color} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : (
                        data?.shifts.data.map((item, rowIndex) => (
                            <DataTable.Row
                                key={item?.id}
                                onPress={() =>
                                    item && item.zReport !== null
                                        ? handleShowShift(item.id)
                                        : undefined
                                }
                                style={
                                    rowIndex % 2 === 1
                                        ? styles.oddRow
                                        : undefined
                                }
                            >
                                <DataTable.Cell>
                                    {item && item.cashRegister.department?.name}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    {item && item.cashRegister.name}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    <Text
                                        ellipsizeMode="middle"
                                        numberOfLines={1}
                                    >
                                        {item &&
                                            formatDateTime(
                                                item.openedAt,
                                                locale as AvailableLocale
                                            )}
                                    </Text>
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    {item &&
                                        item.closedAt &&
                                        formatDateTime(
                                            item.closedAt,
                                            locale as AvailableLocale
                                        )}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    {item?.openedByUser.username}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    {item?.closedByUser?.username}
                                </DataTable.Cell>
                                <DataTable.Cell numeric>
                                    {item &&
                                        formatAmount(item.amount, currency, {
                                            locale: locale as AvailableLocale,
                                        })}
                                </DataTable.Cell>
                                <DataTable.Cell numeric style={styles.sortIcon}>
                                    {item?.zReport !== null && (
                                        <IconButton
                                            color={styles.detailsIcon.color}
                                            name="shift"
                                            onPress={() =>
                                                item && handleShowShift(item.id)
                                            }
                                        />
                                    )}
                                </DataTable.Cell>
                            </DataTable.Row>
                        ))
                    )}

                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.shifts.pagination.pages}
                        itemCount={data?.shifts.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    sortIcon: {
        flexBasis: theme.spacingScale * 6,
        flexShrink: 0,
        flexGrow: 0,
        color: theme.colors.black,
    },

    detailsIcon: {
        color: theme.colors.secondary,
    },

    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
});
